import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  BaseButton,
  RadioButton,
  CloseButton,
  trackResumeDownload,
  useIsMobile,
  useT,
} from '@repo/common';

import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/20px/icon_20px_download.svg';

const PreviewDownloadModal = ({ onClose, onDownload, hideDim = false }) => {
  const isMobile = useIsMobile();
  const t = useT();
  const [selectedOption, setSelectedOption] = useState('original');
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      await onDownload(selectedOption);
      trackResumeDownload({ download_type: selectedOption });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const content = (
    <>
      <div className='w-full flex items-start p-6 gap-2 tabletH:gap-4 border-b justify-between'>
        <div className='flex flex-row tabletH:gap-2 w-full'>
          <div className='flex items-center pr-2 h-[28px]'>
            <DownloadIcon className='text-icon-primary ' />
          </div>
          <div className='flex flex-col items-start tabletH:gap-[2px] w-full'>
            <div className='typo-b14Strong tabletH:typo-b18Strong text-text-primary'>
              {t('Download Resume', '이력서 다운로드')}
            </div>
            <p className='typo-b11 tabletH:typo-b12 text-text-placeholder tabletH:text-text-secondary'>
              {t(
                'Choose a language to download your resume',
                '이력서를 다운로드할 언어를 선택해 주세요',
              )}
            </p>
          </div>
        </div>

        <CloseButton type={isMobile ? 'light' : 'default'} onClick={onClose} />
      </div>

      <div className='flex flex-col items-start p-6 tabletH:pb-[30px] w-full'>
        <div className='flex items-start gap-2 py-2 cursor-pointer transition-all'>
          <RadioButton
            checked={selectedOption === 'original'}
            onChange={() => setSelectedOption('original')}
            ariaLabel={t('Download original resume', '원본 이력서 다운로드')}
          />
          <div className='flex flex-col'>
            <span className='typo-b14Strong tabletH:text-[15px] tabletH:font-semibold text-text-primary'>
              {t('Original (as entered)', '원본 (입력한 내용)')}
            </span>
          </div>
        </div>

        <div className='flex items-start gap-2 py-2 cursor-pointer transition-all'>
          <RadioButton
            checked={selectedOption === 'korean'}
            onChange={() => setSelectedOption('korean')}
            ariaLabel={t(
              'Download Korean translated resume',
              '한국어로 번역된 이력서 다운로드',
            )}
          />
          <div className='flex flex-col'>
            <span className='typo-b14Strong tabletH:text-[15px] tabletH:font-semibold text-text-primary'>
              {t('Korean (Auto-translated)', '한국어 (자동 번역)')}
            </span>
            <div className='typo-b12 tabletH:text-[12px] text-grayscale-gray05 mt-1 tabletH:leading-relaxed'>
              {t(
                'Auto-translation is provided for convenience. Please review before submitting.',
                '편의를 위해 자동 번역이 제공됩니다. 제출하기 전에 내용을 검토해 주세요.',
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='w-full flex justify-end px-6 pb-6'>
        <BaseButton
          onClick={handleDownload}
          width={isMobile ? '100%' : '140px'}
          hideIcon
          isLoading={isLoading}
        >
          {t('Download PDF', 'PDF 다운로드')}
        </BaseButton>
      </div>
    </>
  );

  return ReactDOM.createPortal(
    isMobile ? (
      <div
        className='fixed inset-0 z-[100000000] flex items-end bg-surface-gray40 p-4 pb-[62px]'
        onClick={onClose}
      >
        <div
          className='relative w-full flex flex-col items-center max-w-[420px] bg-grayscale-gray00 rounded-[30px] shadow-popup-02'
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      </div>
    ) : (
      <div
        className={`fixed inset-0 z-[100000000] flex items-center justify-center ${
          hideDim ? '' : 'bg-surface-black60'
        }`}
      >
        <div className='absolute inset-0' onClick={onClose} />

        <div
          className='relative flex flex-col items-center p-0 min-w-[420px] min-h-[320px] max-h-[700px] rounded-[30px]
          bg-grayscale-gray00 border-[1px] border-grayscale-gray02
          shadow-2'
        >
          {content}
        </div>
      </div>
    ),
    document.body,
  );
};

export default PreviewDownloadModal;
